import type { ChatMessage } from "@models/chat";
import { MERMAID_STARTERS } from "./constants";

export interface ExtractResult {
  /** Message text with every mermaid block removed. */
  text: string;
  /** One entry per detected diagram, in source order. */
  mermaid: string[];
}

// Characters that break Mermaid's flowchart label parser when they appear
// unquoted inside a node-shape bracket: parentheses (other shapes), angle
// brackets (HTML), quotes, ampersands, colons, semicolons, hashes.
const NEEDS_QUOTING = /[()<>"&:;#|]/;

function quoteLabel(content: string): string {
  // Unescape any pre-existing escaped quotes, then re-quote cleanly.
  const inner = content.replace(/"/g, "'").trim();
  return `"${inner}"`;
}

// LLMs frequently emit Mermaid that mixes node-shape characters with free-form
// engineering text (`U1 [TPS51125RGER (Buck)]`, `VIN (12V)`). Mermaid's parser
// rejects these because the inner `(` looks like the start of a round node.
// We rewrite unquoted labels to quoted form, which Mermaid accepts as opaque
// text. Also keeps the original `subgraph FOO["title"]` → `subgraph FOO [title]`
// fix because the subgraph line uses different label syntax.
function sanitizeMermaid(body: string): string {
  let out = body;

  // 1. Rectangle labels: nodeId[content] → nodeId["content"] when content has
  //    metachars and isn't already quoted. Also covers "id[label]" inside an
  //    edge expression like `A --> B[label]`.
  out = out.replace(
    /([A-Za-z0-9_])\[([^\]"\n]+)\]/g,
    (match, prefix: string, content: string) => {
      if (!NEEDS_QUOTING.test(content)) return match;
      return `${prefix}[${quoteLabel(content)}]`;
    },
  );

  // 2. Round node labels: nodeId(content) → nodeId("content"). Same rules.
  //    Avoid matching ((double)) circles by requiring a non-paren after `(`.
  out = out.replace(
    /([A-Za-z0-9_])\(([^()"\n][^()"\n]*)\)/g,
    (match, prefix: string, content: string) => {
      if (!NEEDS_QUOTING.test(content)) return match;
      return `${prefix}(${quoteLabel(content)})`;
    },
  );

  // 3. Stadium / pill labels: nodeId([content]) → nodeId(["content"]).
  out = out.replace(
    /([A-Za-z0-9_])\(\[([^\]"\n]+)\]\)/g,
    (match, prefix: string, content: string) => {
      if (!NEEDS_QUOTING.test(content)) return match;
      return `${prefix}([${quoteLabel(content)}])`;
    },
  );

  // 4. Subgraph headers: `subgraph FOO["Title"]` → `subgraph FOO [Title]`.
  //    Mermaid accepts space-separated `[Title]` on subgraph lines but not
  //    quoted-bracket syntax.
  out = out.replace(
    /^(\s*)subgraph\s+([A-Za-z_][\w-]*)\s*\[\s*"?([^\]"\n]*?)"?\s*\]/gm,
    (_m, indent: string, id: string, title: string) =>
      `${indent}subgraph ${id} [${title.trim()}]`,
  );

  return out;
}

/**
 * Pull every ```mermaid fenced block out of a raw LLM response.
 *
 * - Handles fenced blocks with or without the `mermaid` language tag.
 * - Handles indented fences (e.g. inside a list item).
 * - Returns diagrams in the order they appeared in the source.
 * - Falls back to the first bare "graph …" / "flowchart …" line if no fence
 *   is present (legacy safety net — the agent is now instructed to always
 *   fence, but older saved sessions may not).
 */
export function extractMermaid(raw: string): ExtractResult {
  if (!raw) return { text: "", mermaid: [] };

  const starters = MERMAID_STARTERS.join("|");
  // Match any fenced block: explicit ```mermaid or a plain ``` whose body
  // starts with a mermaid keyword. Global + multiline + dotall via [\s\S].
  const fenceRegex = /\`\`\`(?:mermaid)?[^\S\n]*\n([\s\S]*?)\n?\`\`\`/g;
  const starterRegex = new RegExp(String.raw`^\s*(?:${starters})\b`, "i");

  const diagrams: string[] = [];
  // Walk the original string and rebuild it minus the fences so removed
  // blocks don't leave trailing blank lines.
  const pieces: string[] = [];
  let lastEnd = 0;
  for (const match of raw.matchAll(fenceRegex)) {
    const body = match[1].trim();
    // Was this an explicit ```mermaid fence?
    const header = raw.slice(match.index ?? 0, (match.index ?? 0) + 12);
    const isExplicit = /^```mermaid/i.test(header);
    const looksLikeMermaid = isExplicit || starterRegex.test(body);
    if (!looksLikeMermaid) continue;

    diagrams.push(sanitizeMermaid(body));
    pieces.push(raw.slice(lastEnd, match.index ?? 0));
    lastEnd = (match.index ?? 0) + match[0].length;
  }
  if (diagrams.length > 0) {
    pieces.push(raw.slice(lastEnd));
    const text = pieces
      .join("")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    return { text, mermaid: diagrams };
  }

  // Fallback: bare keyword, no fence. Take everything from that point on.
  for (const kw of [
    "graph LR",
    "graph TD",
    "graph TB",
    "flowchart LR",
    "flowchart TD",
  ]) {
    const idx = raw.indexOf(kw);
    if (idx !== -1) {
      return {
        text: raw.slice(0, idx).trim(),
        mermaid: [sanitizeMermaid(raw.slice(idx).trim())],
      };
    }
  }

  return { text: raw, mermaid: [] };
}

/**
 * Normalise messages loaded from history so their mermaid diagrams are
 * rendered as SVG instead of leaking into the markdown. The backend persists
 * the raw LLM text with fenced ```mermaid blocks still embedded and sets
 * `mermaid: null`, expecting the client to re-derive. Apply this to every
 * assistant message before handing them to the store.
 */
export function normalizeMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.map((m) => {
    if (m.role !== "assistant") return m;
    // Already extracted (live stream path) — leave alone.
    if (Array.isArray(m.mermaid) && m.mermaid.length > 0) return m;
    const { text, mermaid } = extractMermaid(m.content);
    if (mermaid.length === 0) return { ...m, mermaid: [] };
    return { ...m, content: text, mermaid };
  });
}
